<script setup lang="ts">
import { Calendar, Sparkles, Bug, Wrench, Zap } from 'lucide-vue-next'
import PublicLayout from '@/components/PublicLayout.vue'

interface ChangelogEntry {
  version: string
  date: string
  type: 'feature' | 'fix' | 'improvement' | 'breaking'
  title: string
  description: string
  details?: string[]
}

const changelog: ChangelogEntry[] = [
  {
    version: '1.8.0',
    date: '2025-11-27',
    type: 'feature',
    title: 'Image Compression & Improved Bulk Generation',
    description: 'Compress images in generated documents and more reliable bulk generation.',
    details: [
      'New image compression option (Pro) to reduce document file size using WebP format',
      'Configurable compression quality (1-100) in template settings',
      'More reliable background generation using queue-based processing',
      '30MB image size limit per document with warning when exceeded'
    ]
  },
  {
    version: '1.7.0',
    date: '2025-11-27',
    type: 'feature',
    title: 'Changelog Page & Date Formatting',
    description: 'Public changelog page and improved date field formatting using Lark field patterns.',
    details: [
      'DateTime fields now use Lark\'s configured date formatter patterns',
      'Created/Modified time fields respect field-level date format settings',
    ]
  },
  {
    version: '1.6.0',
    date: '2025-11-25',
    type: 'feature',
    title: 'Image Placeholders & Boolean Conditionals',
    description: 'Replace placeholder images in templates and improved conditional logic for checkbox fields.',
    details: [
      'Image placeholder replacement using {ImageField} syntax in templates',
      'Truthiness-based conditionals for non-empty value checks',
      'Checkbox fields now properly output true/false boolean values',
      'Improved conditional logic for empty vs non-empty field values'
    ]
  },
  {
    version: '1.4.1',
    date: '2025-11-18',
    type: 'fix',
    title: 'Currency Formatting & Field Rename Handling',
    description: 'Fixed locale-aware currency formatting and improved error handling for renamed fields.',
    details: [
      'Currency fields now format according to user locale settings',
      'Graceful error handling when Lark Base fields are renamed',
      'Better error messages for field access issues'
    ]
  },
  {
    version: '1.4.0',
    date: '2025-11-18',
    type: 'feature',
    title: 'Excel-Style Number Formatting',
    description: 'Automatic number formatting based on Lark field formatters.',
    details: [
      'Number formatting inherited from Lark field settings',
      'Support for decimal places and thousands separators',
      'Currency symbol positioning based on field configuration'
    ]
  },
  {
    version: '1.3.0',
    date: '2025-11-11',
    type: 'feature',
    title: 'HTTP Trigger & Webhook API',
    description: 'Trigger document generation from Lark Base Automation workflows.',
    details: [
      'HTTP Trigger endpoint for automated document generation',
      'Secure API key authentication for webhook calls',
      'Integration with Lark Base Automation actions',
      'Dismissible Pro banner and improved My Templates view'
    ]
  },
  {
    version: '1.2.0',
    date: '2025-11-10',
    type: 'feature',
    title: 'My Templates & Real-time Field Detection',
    description: 'View all templates across bases and automatic field change detection.',
    details: [
      'My Templates page showing all templates across your Lark Bases',
      'Real-time field change detection in template config',
      'Configurable retention settings for templates and generations',
      'Rate limiting for free tier document generation',
      'Public welcome, pricing, and legal pages'
    ]
  },
  {
    version: '1.1.0',
    date: '2025-11-05',
    type: 'improvement',
    title: 'Simplified Download Architecture',
    description: 'Improved download URLs and cleaner user interface.',
    details: [
      'Simplified download URL architecture',
      'Improved UI for document downloads',
      'Better file handling and naming'
    ]
  },
  {
    version: '1.0.0',
    date: '2025-11-04',
    type: 'feature',
    title: 'PDF Generation & Webhook Notifications',
    description: 'Generate documents as PDF and receive webhook notifications.',
    details: [
      'PDF generation support (converts DOCX to PDF)',
      'Webhook notifications for Pro users after generation',
      'Unified generation API architecture',
      'Enhanced admin panel with tenant management',
      'Auto-select first record for filename preview'
    ]
  },
  {
    version: '0.9.0',
    date: '2025-11-03',
    type: 'feature',
    title: 'Quota System & Generation History',
    description: 'Monthly quota tracking and dedicated generations history page.',
    details: [
      'Monthly generation quota with automated CRON resets',
      'Configurable template limits per tenant',
      'Dedicated Generations history page with pagination',
      'Template validation with placeholder error detection',
      'Personal base token validation and navigation guard',
      'Tiered document generation with free tier delay'
    ]
  },
  {
    version: '0.8.0',
    date: '2025-10-31',
    type: 'feature',
    title: 'Bulk Document Generation',
    description: 'Generate documents for multiple records at once.',
    details: [
      'Bulk generation with foreground (ZIP download) and background modes',
      'Multi-environment support (dev/staging/production)',
      'Select multiple records using Lark Base checkboxes'
    ]
  },
  {
    version: '0.7.0',
    date: '2025-10-30',
    type: 'feature',
    title: 'Background Generation & TypeScript Migration',
    description: 'Auto-save to Lark Base attachments and complete TypeScript rewrite.',
    details: [
      'Background document generation with auto-save to Lark attachment fields',
      'Complete migration to TypeScript for all Vue components',
      'Redesigned Pro Features UI in Template Setup',
      'Migration from composables to Pinia stores'
    ]
  },
  {
    version: '0.5.0',
    date: '2025-10-26',
    type: 'feature',
    title: 'Nested Fields & Circular Reference Support',
    description: 'Support for deeply nested linked records with circular reference handling.',
    details: [
      'Circular reference detection and handling in nested fields',
      'Depth-based UI collapse for nested field trees',
      'Improved linked record traversal'
    ]
  }
]

const getTypeIcon = (type: string) => {
  switch (type) {
    case 'feature': return Sparkles
    case 'fix': return Bug
    case 'improvement': return Wrench
    case 'breaking': return Zap
    default: return Sparkles
  }
}

const getTypeColor = (type: string) => {
  switch (type) {
    case 'feature': return 'bg-green-500'
    case 'fix': return 'bg-red-500'
    case 'improvement': return 'bg-blue-500'
    case 'breaking': return 'bg-orange-500'
    default: return 'bg-gray-500'
  }
}

const getTypeBgColor = (type: string) => {
  switch (type) {
    case 'feature': return 'bg-green-50 border-green-200'
    case 'fix': return 'bg-red-50 border-red-200'
    case 'improvement': return 'bg-blue-50 border-blue-200'
    case 'breaking': return 'bg-orange-50 border-orange-200'
    default: return 'bg-gray-50 border-gray-200'
  }
}

const getTypeLabel = (type: string) => {
  switch (type) {
    case 'feature': return 'New Feature'
    case 'fix': return 'Bug Fix'
    case 'improvement': return 'Improvement'
    case 'breaking': return 'Breaking Change'
    default: return 'Update'
  }
}
</script>

<template>
  <PublicLayout>
    <div class="max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <!-- Header -->
      <div class="text-center pt-4 sm:pt-8 mb-8 sm:mb-12">
        <h1 class="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-3">Changelog</h1>
        <p class="text-lg sm:text-xl text-gray-600">Track all updates and improvements to AutomaDoc</p>
      </div>

      <!-- Timeline -->
      <div class="relative">
        <!-- Vertical Line -->
        <div class="absolute left-4 sm:left-8 top-0 bottom-0 w-0.5 bg-gray-200"></div>

        <!-- Coming Soon Entry -->
        <div class="relative pl-12 sm:pl-20 mb-8">
          <!-- Timeline Dot - Dashed/pulsing for upcoming -->
          <div
            class="absolute left-2 sm:left-6 w-5 h-5 rounded-full border-4 border-white shadow-md bg-purple-400 animate-pulse"
            style="top: 6px;"
          ></div>

          <!-- Coming Soon Card -->
          <div class="border-2 border-dashed border-purple-300 bg-purple-50 rounded-xl p-5 sm:p-6">
            <div class="flex items-center gap-3 text-purple-600">
              <Sparkles :size="20" />
              <span class="font-semibold">More updates coming soon...</span>
            </div>
          </div>
        </div>

        <!-- Timeline Entries -->
        <div class="space-y-8">
          <div
            v-for="(entry, index) in changelog"
            :key="entry.version"
            class="relative pl-12 sm:pl-20"
          >
            <!-- Timeline Dot -->
            <div
              :class="[
                'absolute left-2 sm:left-6 w-5 h-5 rounded-full border-4 border-white shadow-md',
                getTypeColor(entry.type)
              ]"
              style="top: 6px;"
            ></div>

            <!-- Content Card -->
            <div
              :class="[
                'border rounded-xl p-5 sm:p-6 transition-shadow hover:shadow-lg',
                getTypeBgColor(entry.type)
              ]"
            >
              <!-- Header -->
              <div class="flex flex-wrap items-center gap-3 mb-3">
                <!-- Version Badge -->
                <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-gray-800 text-white">
                  v{{ entry.version }}
                </span>

                <!-- Type Badge -->
                <span
                  :class="[
                    'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium text-white',
                    getTypeColor(entry.type)
                  ]"
                >
                  <component :is="getTypeIcon(entry.type)" :size="14" />
                  {{ getTypeLabel(entry.type) }}
                </span>

                <!-- Date -->
                <span class="inline-flex items-center gap-1.5 text-sm text-gray-500">
                  <Calendar :size="14" />
                  {{ entry.date }}
                </span>
              </div>

              <!-- Title -->
              <h3 class="text-lg sm:text-xl font-bold text-gray-800 mb-2">
                {{ entry.title }}
              </h3>

              <!-- Description -->
              <p class="text-gray-700 mb-4">
                {{ entry.description }}
              </p>

              <!-- Details List -->
              <ul v-if="entry.details && entry.details.length > 0" class="space-y-2">
                <li
                  v-for="(detail, detailIndex) in entry.details"
                  :key="detailIndex"
                  class="flex items-start gap-2 text-sm text-gray-600"
                >
                  <span class="text-gray-400 mt-1">•</span>
                  {{ detail }}
                </li>
              </ul>
            </div>

            <!-- Connector for last item -->
            <div
              v-if="index === changelog.length - 1"
              class="absolute left-2 sm:left-6 w-5 h-5 rounded-full bg-gray-300 border-4 border-white shadow-sm"
              style="bottom: -40px;"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </PublicLayout>
</template>
